'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import { GARDEN_TEMPLATES } from '@/data/gardenTemplates';
import type { GardenTemplate } from '@/data/gardenTemplates';
import type { CanonicalPlant } from '@/lib/normalizePlant';
import { getToken, isProUser } from '@/lib/auth';
import { useAuth } from '@/components/AuthProvider';
import { useAuthGate } from '@/context/AuthGateContext';
import { addPlantingLog } from '@/lib/plantingTracker';
import type { PlantingLog } from '@/lib/plantingTracker';
import { getPlans, savePlan, createDefaultPlan } from '@/data/gardenPlanner';
import { saveGardenPlans } from '@/lib/dataSync';
import { useUserData } from '@/context/UserDataContext';
import { createBedFromTemplate } from '@/lib/templateToPlanner';
import {
  buildCanonicalPlantLookup,
  listBuiltInCanonicalPlants,
  resolveCanonicalPlantsById,
} from '@/lib/plantCatalog';
import { todayLocalISO } from '@/lib/localDate';

// ── Icons ─────────────────────────────────────────────────────────────────────

function LeafIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M8 14V8" />
      <path d="M8 8C6 8 3 7 3 4.5C3 2.5 5.5 2 7 3.5C7.5 4.5 8 6.5 8 8Z" />
      <path d="M8 11C10 11 13 10 13 7.5C13 5.5 10.5 5 9 6.5C8.5 7.5 8 9.5 8 11Z" />
    </svg>
  );
}

function InfoIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="7" cy="7" r="5.5" />
      <path d="M7 6.5v4" />
      <circle cx="7" cy="4.5" r="0.5" fill="currentColor" stroke="none" />
    </svg>
  );
}

function CheckCircleLargeIcon() {
  return (
    <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
      <circle cx="24" cy="24" r="22" fill="#E8F0E4" stroke="#2D5016" strokeWidth="2" />
      <path d="M14 24L21 31L34 18" stroke="#2D5016" strokeWidth="2.5"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4L14 14M14 4L4 14" />
    </svg>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function todayISO(): string {
  return todayLocalISO();
}

function parseDaysToMaturity(str: string): number {
  const yearMatch = str.match(/(\d+)(?:\s*[-–]\s*(\d+))?\s*years?/i);
  if (yearMatch) {
    const lo = parseInt(yearMatch[1]);
    const hi = yearMatch[2] ? parseInt(yearMatch[2]) : lo;
    return Math.round(((lo + hi) / 2) * 365);
  }
  const dayRange = str.match(/(\d+)\s*[-–]\s*(\d+)/);
  if (dayRange) return Math.round((parseInt(dayRange[1]) + parseInt(dayRange[2])) / 2);
  const daySingle = str.match(/(\d+)\s*days?/i);
  if (daySingle) return parseInt(daySingle[1]);
  return 75;
}

function estimatedFirstHarvest(template: GardenTemplate, plantLookup: Map<string, CanonicalPlant>): string {
  const days = template.plants.reduce((max, tp) => {
    const plant = plantLookup.get(tp.plantId);
    if (!plant) return max;
    return Math.max(max, parseDaysToMaturity(plant.daysToMaturity));
  }, 0);
  if (days === 0) return 'Varies by plant';
  if (days < 35) return 'Under 5 weeks';
  if (days < 60) return `Around ${Math.round(days / 7)} weeks`;
  if (days < 365) return `Around ${Math.round(days / 30)} months`;
  return `${Math.round(days / 365)} year${days >= 730 ? 's' : ''}`;
}

function seasonLabel(season: GardenTemplate['season']): string {
  return { spring: 'Spring', summer: 'Summer', fall: 'Fall', 'year-round': 'Year-round' }[season];
}

// ── Badge styles ──────────────────────────────────────────────────────────────

const CATEGORY_BADGE: Record<GardenTemplate['category'], { label: string; cls: string }> = {
  'in-ground': { label: 'In-Ground',  cls: 'bg-deep-green text-white'     },
  'raised-bed': { label: 'Raised Bed', cls: 'bg-warm-brown text-white'     },
  'container':  { label: 'Container',  cls: 'bg-sage text-white'           },
};

const SOIL_NOTE: Record<GardenTemplate['category'], string> = {
  'in-ground':  'Preparing in-ground soil? Work in 2–3 inches of compost before planting.',
  'raised-bed': 'Fill your raised bed with a mix of quality compost and topsoil before planting.',
  'container':  'Use a quality potting mix, not garden soil, which compacts in containers.',
};

// ── CategoryNoteTooltip ───────────────────────────────────────────────────────

function CategoryNoteTooltip({ note }: { note: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    if (open) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <span ref={ref} className="relative inline-flex">
      <button
        onClick={e => { e.stopPropagation(); setOpen(v => !v); }}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        className="text-sage hover:text-warm-brown transition-colors"
        aria-label="Category note"
      >
        <InfoIcon />
      </button>
      {open && (
        <span
          className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-60 bg-[#FFF9F0]
                     border border-[#E8D5B8] rounded-lg px-3 py-2.5 shadow-md z-10
                     pointer-events-none block"
        >
          <p className="font-body text-[12px] text-warm-brown leading-relaxed">{note}</p>
          <span className="absolute top-full left-1/2 -translate-x-1/2 w-2 h-2 bg-[#FFF9F0]
                           border-r border-b border-[#E8D5B8] rotate-45 -mt-1 block" />
        </span>
      )}
    </span>
  );
}

// ── Preview Modal ─────────────────────────────────────────────────────────────

function PreviewModal({
  template,
  plantLookup,
  onClose,
  onApply,
}: {
  template: GardenTemplate;
  plantLookup: Map<string, CanonicalPlant>;
  onClose: () => void;
  onApply: () => void;
}) {
  const badge = CATEGORY_BADGE[template.category];
  const harvest = estimatedFirstHarvest(template, plantLookup);

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal card */}
      <div
        className="fixed z-[51] bg-white rounded-2xl shadow-xl overflow-y-auto
                   flex flex-col"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: '560px',
          maxHeight: 'calc(100vh - 48px)',
          padding: '24px',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-sage hover:text-dark-text transition-colors"
        >
          <CloseIcon />
        </button>

        {/* Emoji */}
        <div className="text-center mb-3">
          <span style={{ fontSize: 64, lineHeight: 1 }}>{template.emoji}</span>
        </div>

        {/* Category badge */}
        <div className="flex justify-center mb-3">
          <span className={`font-body text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wide ${badge.cls}`}>
            {badge.label}
          </span>
        </div>

        {/* Name */}
        <h2 className="font-heading text-[24px] text-dark-text text-center leading-snug mb-2">
          {template.name}
        </h2>

        {/* Description */}
        <p className="font-body text-[15px] text-sage leading-relaxed text-center mb-4">
          {template.description}
        </p>

        {/* Category note */}
        {template.categoryNote && (
          <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-4 py-3 mb-5">
            <div className="flex items-start gap-2">
              <span className="text-sage mt-0.5 flex-shrink-0"><LeafIcon size={14} /></span>
              <p className="font-body text-[13px] text-sage leading-relaxed">
                {template.categoryNote}
              </p>
            </div>
          </div>
        )}

        {/* Plants */}
        <div className="mb-5">
          <h3 className="font-body text-[13px] font-semibold text-dark-text uppercase tracking-wider mb-3">
            Plants in this template
          </h3>
          <ul className="space-y-3">
            {template.plants.map(tp => (
              <li key={tp.plantId} className="flex items-start gap-2.5">
                <span className="text-sage mt-0.5 flex-shrink-0"><LeafIcon size={14} /></span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-body text-[14px] font-semibold text-dark-text">
                      {tp.plantName}
                    </span>
                    <span className="font-body text-[12px] text-sage">×{tp.quantity}</span>
                  </div>
                  {tp.notes && (
                    <p className="font-body text-[12px] text-sage italic leading-snug mt-0.5">
                      {tp.notes}
                    </p>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* What you will need */}
        <div className="bg-cream rounded-xl px-4 py-4 mb-6">
          <h3 className="font-body text-[13px] font-semibold text-dark-text uppercase tracking-wider mb-3">
            What you will need
          </h3>
          <ul className="space-y-1.5">
            <li className="flex items-center gap-2 font-body text-[13px] text-sage">
              <span>🌱</span>
              <span>{template.recommendedSize}</span>
            </li>
            <li className="flex items-center gap-2 font-body text-[13px] text-sage">
              <span>📅</span>
              <span>Best started in {seasonLabel(template.season)}</span>
            </li>
            <li className="flex items-center gap-2 font-body text-[13px] text-sage">
              <span>⏱</span>
              <span>First harvest: {harvest}</span>
            </li>
          </ul>
        </div>

        {/* Buttons */}
        <button
          onClick={onApply}
          className="w-full bg-deep-green text-white font-body font-semibold text-[15px]
                     py-3.5 rounded-lg hover:opacity-90 transition-opacity mb-3"
        >
          {template.category === 'container' ? 'Start with this template' : 'Plan This Garden →'}
        </button>
        <button
          onClick={onClose}
          className="w-full font-body text-[14px] text-sage hover:text-dark-text
                     transition-colors py-2 text-center"
        >
          Close
        </button>
      </div>
    </>
  );
}

// ── Apply Modal ───────────────────────────────────────────────────────────────

function ApplyModal({
  template,
  onClose,
  onSuccess,
}: {
  template: GardenTemplate;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const [step, setStep]           = useState<'confirm' | 'success'>('confirm');
  const [plantDate, setPlantDate] = useState(todayISO());
  const [location, setLocation]   = useState('');
  const [applying, setApplying]   = useState(false);
  const [planted, setPlanted]     = useState(0);

  async function handleApply() {
    setApplying(true);
    const ts = new Date().toISOString();
    for (const tp of template.plants) {
      const log: PlantingLog = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
        plantId: tp.plantId,
        plantName: tp.plantName,
        plantedDate: plantDate,
        location: location.trim() || undefined,
        notes: tp.notes || undefined,
        status: 'growing',
        quantity: tp.quantity,
        createdAt: ts,
        templateId: template.id,
      };
      await addPlantingLog(log);
      setPlanted(prev => prev + tp.quantity);
    }
    setApplying(false);
    setStep('success');
  }

  const totalPlants = template.plants.reduce((sum, tp) => sum + tp.quantity, 0);
  const badge = CATEGORY_BADGE[template.category];

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black/50 z-50 transition-opacity"
        onClick={step === 'success' ? undefined : onClose}
      />

      {/* Modal card */}
      <div
        className="fixed z-[51] bg-white rounded-2xl shadow-xl overflow-y-auto flex flex-col"
        style={{
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 'calc(100% - 32px)',
          maxWidth: '480px',
          maxHeight: 'calc(100vh - 48px)',
          padding: '28px 24px',
        }}
        onClick={e => e.stopPropagation()}
      >

        {/* ── Confirm step ── */}
        {step === 'confirm' && (
          <>
            <div className="text-center mb-5">
              <span style={{ fontSize: 48, lineHeight: 1 }}>{template.emoji}</span>
              <div className="mt-2">
                <span className={`font-body text-[11px] font-bold px-3 py-1 rounded-full uppercase tracking-wide ${badge.cls}`}>
                  {badge.label}
                </span>
              </div>
            </div>

            <h2 className="font-heading text-[20px] text-dark-text text-center mb-1">
              {template.name}
            </h2>
            <p className="font-body text-[14px] text-sage text-center mb-6">
              {totalPlants} plants will be added to your garden
            </p>

            {/* Date */}
            <div className="mb-4">
              <label className="block font-body text-[12px] font-semibold text-sage
                                uppercase tracking-wider mb-1.5">
                When did you plant, or when do you plan to plant?
              </label>
              <input
                type="date"
                value={plantDate}
                onChange={e => setPlantDate(e.target.value)}
                className="w-full font-body text-[14px] text-dark-text bg-white border border-sand
                           rounded-lg px-3 py-2.5 focus:outline-none focus:border-sage transition-colors"
              />
            </div>

            {/* Location */}
            <div className="mb-5">
              <label className="block font-body text-[12px] font-semibold text-sage
                                uppercase tracking-wider mb-1.5">
                Where is this garden? <span className="font-normal normal-case">(optional)</span>
              </label>
              <input
                type="text"
                value={location}
                onChange={e => setLocation(e.target.value)}
                placeholder="Back yard, balcony, kitchen windowsill, front border..."
                className="w-full font-body text-[14px] text-dark-text bg-white border border-sand
                           rounded-lg px-3 py-2.5 focus:outline-none focus:border-sage transition-colors
                           placeholder:text-sand"
              />
            </div>

            {/* Soil note */}
            <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-4 py-3 mb-6">
              <div className="flex items-start gap-2">
                <span className="text-sage mt-0.5 flex-shrink-0"><LeafIcon size={14} /></span>
                <p className="font-body text-[13px] text-sage leading-relaxed">
                  {SOIL_NOTE[template.category]}
                </p>
              </div>
            </div>

            <button
              onClick={() => void handleApply()}
              disabled={applying || !plantDate}
              className="w-full bg-deep-green text-white font-body font-semibold text-[15px]
                         py-3.5 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60 mb-3"
            >
              {applying ? `Adding plants… (${planted}/${totalPlants})` : 'Apply Template'}
            </button>
            <button
              onClick={onClose}
              className="w-full font-body text-[14px] text-sage hover:text-dark-text
                         transition-colors py-2 text-center"
            >
              Cancel
            </button>
          </>
        )}

        {/* ── Success step ── */}
        {step === 'success' && (
          <div className="flex flex-col items-center text-center">
            <div className="mb-5">
              <CheckCircleLargeIcon />
            </div>
            <h2 className="font-heading text-[22px] text-dark-text mb-2">
              Your {template.name} is all set
            </h2>
            <p className="font-body text-[15px] text-sage leading-relaxed mb-8 max-w-sm">
              We have added {totalPlants} plant{totalPlants !== 1 ? 's' : ''} to your garden
              and created your first tasks. Container gardens are tracked in your planting log — add
              them to your garden planner manually if you would like to visualise them.
            </p>
            <Link
              href="/dashboard"
              className="w-full bg-deep-green text-white font-body font-semibold text-[15px]
                         py-3.5 rounded-lg hover:opacity-90 transition-opacity mb-4 text-center block"
            >
              View My Garden
            </Link>
            <button
              onClick={() => { onSuccess(); onClose(); }}
              className="font-body text-[14px] text-sage hover:text-dark-text transition-colors"
            >
              Browse more templates
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ── Template Card ─────────────────────────────────────────────────────────────

function TemplateCard({
  template,
  locked,
  onPreview,
  onApply,
}: {
  template: GardenTemplate;
  locked: boolean;
  onPreview: () => void;
  onApply: () => void;
}) {
  const badge = CATEGORY_BADGE[template.category];
  const totalPlants = template.plants.reduce((sum, tp) => sum + tp.quantity, 0);
  const isContainer = template.category === 'container';

  return (
    <div className="bg-white border border-sand rounded-2xl overflow-hidden flex flex-col">
      {/* Top section — cream background with emoji */}
      <div className="relative bg-[#F5F0E8] flex items-center justify-center" style={{ height: 120 }}>
        <span className="absolute top-3 left-3">
          <span className={`font-body text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide ${badge.cls}`}>
            {badge.label}
          </span>
        </span>
        {locked && (
          <span className="absolute top-3 right-3 bg-warm-brown text-white font-body text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wide">
            Pro
          </span>
        )}
        <span style={{ fontSize: 48, lineHeight: 1 }}>{template.emoji}</span>
      </div>

      {/* Card body */}
      <div className="flex flex-col flex-1 p-5">

        {/* Name + tagline */}
        <h3 className="font-heading font-bold text-[18px] text-dark-text leading-snug mb-1">
          {template.name}
        </h3>
        <p className="font-body text-[13px] text-sage italic mb-3">
          {template.tagline}
        </p>

        {/* Tags */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          {template.tags.map(tag => (
            <span key={tag}
              className="font-body text-[11px] text-sage bg-light-sage px-2 py-0.5 rounded-full capitalize">
              {tag}
            </span>
          ))}
        </div>

        {/* Meta */}
        <div className="space-y-1 mb-4">
          <div className="font-body text-[12px] text-sage flex items-center gap-1.5">
            <span>🌱</span>
            <span>{template.recommendedSize}</span>
          </div>
          <div className="font-body text-[12px] text-sage flex items-center gap-1.5">
            <span>🌿</span>
            <span>{totalPlants} plants included</span>
            {template.categoryNote && (
              <span className="ml-0.5">
                <CategoryNoteTooltip note={template.categoryNote} />
              </span>
            )}
          </div>
        </div>

        {/* Buttons */}
        <div className="mt-auto space-y-2">
          <button
            onClick={onPreview}
            className="w-full font-body text-[14px] font-semibold text-sage border border-sage
                       py-2.5 rounded-lg hover:bg-light-sage transition-colors"
          >
            Preview
          </button>
          <button
            onClick={onApply}
            className={`w-full font-body text-[14px] font-semibold py-2.5 rounded-lg transition-opacity ${
              locked
                ? 'text-warm-brown bg-[#FFF0E0] border border-[#E8C898] hover:opacity-80'
                : 'text-white bg-deep-green hover:opacity-90'
            }`}
          >
            {locked ? 'Available with Pro' : isContainer ? 'Start with this template' : 'Plan This Garden →'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

type CategoryFilter = 'all' | 'in-ground' | 'raised-bed' | 'container';

const FILTERS: { label: string; value: CategoryFilter }[] = [
  { label: 'All',        value: 'all'       },
  { label: 'In-Ground',  value: 'in-ground' },
  { label: 'Raised Bed', value: 'raised-bed'},
  { label: 'Container',  value: 'container' },
];

export default function TemplatesPage() {
  const { user } = useAuth();
  const isPro = user ? isProUser(user) : false;
  const { triggerAuthGate } = useAuthGate();
  const { refreshAll } = useUserData();
  const router = useRouter();

  const [activeFilter, setActiveFilter]     = useState<CategoryFilter>('all');
  const [previewTemplate, setPreviewTemplate] = useState<GardenTemplate | null>(null);
  const [applyTemplate, setApplyTemplate]   = useState<GardenTemplate | null>(null);
  const [plannerLoading, setPlannerLoading] = useState<string | null>(null); // templateId being processed
  const [plantLookup, setPlantLookup] = useState<Map<string, CanonicalPlant>>(
    () => buildCanonicalPlantLookup(listBuiltInCanonicalPlants()),
  );

  useEffect(() => {
    let cancelled = false;
    const templatePlantIds = GARDEN_TEMPLATES.flatMap(template =>
      template.plants.map(plant => plant.plantId),
    );

    void resolveCanonicalPlantsById(templatePlantIds, {
      includeDraftGenerated: true,
    }).then(plants => {
      if (!cancelled) setPlantLookup(buildCanonicalPlantLookup(plants));
    }).catch(() => {
      if (!cancelled) setPlantLookup(buildCanonicalPlantLookup(listBuiltInCanonicalPlants()));
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const filtered = activeFilter === 'all'
    ? GARDEN_TEMPLATES
    : GARDEN_TEMPLATES.filter(t => t.category === activeFilter);

  async function startApply(template: GardenTemplate) {
    if (!getToken()) {
      triggerAuthGate();
      return;
    }

    if (!isPro && template.id !== 'pizza-garden') {
      router.push('/pricing');
      return;
    }

    if (template.category === 'container') {
      // Container templates: keep the old direct-apply flow
      setPreviewTemplate(null);
      setApplyTemplate(template);
      return;
    }

    // In-ground and raised-bed templates: create planner bed and navigate
    setPreviewTemplate(null);
    setPlannerLoading(template.id);

    try {
      const bed = createBedFromTemplate(template, plantLookup);
      const plans = getPlans();

      let targetPlan;
      if (plans.length > 0) {
        targetPlan = {
          ...plans[0],
          beds: [...plans[0].beds, bed],
          updatedAt: new Date().toISOString(),
        };
      } else {
        const freshPlan = createDefaultPlan();
        // Replace the default bed with our template bed
        targetPlan = { ...freshPlan, beds: [bed], updatedAt: new Date().toISOString() };
      }

      savePlan(targetPlan);
      await saveGardenPlans(getPlans()); // immediate write so planner reads fresh data
      await refreshAll();
      router.push(`/garden?tab=planner&newBed=${bed.id}&template=${template.id}`);
    } finally {
      setPlannerLoading(null);
    }
  }

  return (
    <div className="max-w-5xl">

      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading text-[32px] leading-tight text-dark-text">
          Garden Templates
        </h1>
        <p className="font-body text-[16px] text-sage mt-1">
          Choose a starting point and grow from there. Every template is fully customisable.
        </p>
      </div>

      {/* Philosophy note */}
      <div className="bg-light-sage border border-[#C5D9B8] rounded-xl px-5 py-4 mb-7 flex items-start gap-3">
        <span className="text-sage mt-0.5 flex-shrink-0"><LeafIcon size={16} /></span>
        <p className="font-body text-[14px] text-sage leading-relaxed">
          You do not need raised beds to have a beautiful, productive garden. Most of our templates
          are designed for in-ground growing — the most natural way to garden, and the one that best
          connects your plants to the living soil ecosystem. Raised beds are included where they
          genuinely make sense. Containers are here for gardeners without outdoor space.
        </p>
      </div>

      {/* Category filter pills */}
      <div className="flex flex-wrap gap-2 mb-7">
        {FILTERS.map(f => (
          <button
            key={f.value}
            onClick={() => setActiveFilter(f.value)}
            className={[
              'font-body text-[14px] font-semibold px-4 py-2 rounded-full transition-colors',
              activeFilter === f.value
                ? 'bg-deep-green text-white'
                : 'bg-light-sage text-dark-text hover:bg-[#d8ebd0]',
            ].join(' ')}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* Template grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {filtered.map(template => (
          <TemplateCard
            key={template.id}
            template={template}
            locked={!isPro && template.id !== 'pizza-garden'}
            onPreview={() => setPreviewTemplate(template)}
            onApply={() => !plannerLoading && startApply(template)}
          />
        ))}
      </div>

      {/* Soil calculator nudge */}
      <p className="font-body text-[13px] text-sage mt-4">
        Need to know how much soil to buy?{' '}
        <Link href="/tools/soil-calculator" className="text-sage hover:text-deep-green underline underline-offset-2 transition-colors">
          Use our Soil Calculator →
        </Link>
      </p>

      {/* Preview modal */}
      {previewTemplate && (
        <PreviewModal
          template={previewTemplate}
          plantLookup={plantLookup}
          onClose={() => setPreviewTemplate(null)}
          onApply={() => startApply(previewTemplate)}
        />
      )}

      {/* Apply modal */}
      {applyTemplate && (
        <ApplyModal
          template={applyTemplate}
          onClose={() => setApplyTemplate(null)}
          onSuccess={() => setApplyTemplate(null)}
        />
      )}
    </div>
  );
}
